declare const global_url_base;

const COOKIE_PREFIX = 'brand-model-';

interface Entry {
  label: string;
  value: number;

  [key: string]: any;
}

type FieldViewOption = Entry | Entry[];

interface FieldView {

  onchange: (this: FieldView, value: number) => void;

  loadOptions(options: FieldViewOption[], defaultValue?: number);

  setValue(value: number);

  getValue(): number;

  enable();

  disable();

  render();
}

class ChoiceView implements FieldView {

  private options;
  private value: number;
  public onchange: (this: ChoiceView, value: number) => void;

  constructor(private el: HTMLElement) {
    this.options = [];
    el.addEventListener('change', (e) => {
      const eventTarget = e.currentTarget as HTMLSelectElement
      this.value = Number(eventTarget.value);
      if (this.onchange) {
        this.onchange(this.value);
      }
    });
  }

  disable() {
    this.el.setAttribute('disabled', 'disabled');
  }

  enable() {
    this.el.removeAttribute('disabled');
  }

  loadOptions(data: FieldViewOption[], defaultValue: number) {
    this.options = data;

    if (typeof (defaultValue) !== 'undefined') {
      this.value = defaultValue;
    }
  }

  setValue(value: number) {
    this.value = value;
  }

  getValue() {
    return this.value;
  }

  render() {
    const selectedId = this.value,
      select = this.el;

    select.innerHTML = '';

    const firstOption = document.createElement('option');
    firstOption.text = select.getAttribute('data-placeholder');
    select.append(firstOption);

    const createOption = function (parent, choice) {
      const opt = document.createElement('option');
      opt.value = choice.value;
      opt.innerText = choice.label;
      if (choice.value === selectedId) {
        opt.selected = true;
      }
      parent.appendChild(opt);
    };

    for (const i in this.options) if (this.options.hasOwnProperty(i)) {
      const entry = this.options[i];

      if (entry instanceof Array) {
        const optgroup = document.createElement('optgroup') as HTMLElement,
          op = optgroup as HTMLOptGroupElement;
        op.label = i;
        for (let j = 0; j < entry.length; j++) {
          createOption(optgroup, entry[j]);
        }
        select.append(optgroup);
      } else {
        createOption(select, entry);
      }
    }

    select.focus();
  }

}

export default class ProductSearchBrandFormView {

  private data: {
    category: number,
    brand: number,
    model: number
  };

  private readonly hasModel;
  private readonly brandId: number;
  private readonly modelId: number;
  private readonly modelView: FieldView;

  constructor(private readonly el: HTMLElement,
              private readonly useCookie: boolean,
              private readonly submitButton?: HTMLButtonElement,
              private readonly resetButton?: HTMLButtonElement) {
    this.hasModel = this.el.getAttribute('data-type');

    const brandView = this.el.querySelector('[data-target="brand"]') as HTMLElement,
      toggleBtn = brandView.querySelector('.btn.toggle') as HTMLElement;

    if (toggleBtn) {
      toggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const eventTarget = e.currentTarget as HTMLInputElement;
        eventTarget.nextElementSibling.classList.toggle('open');
      });
    }

    document.addEventListener('click', (e) => {
      if (!e.defaultPrevented) {
        brandView.querySelector('ul').classList.remove('open');
      }
    });

    const modelElem = this.el.querySelector('[data-target="model"]') as HTMLElement;

    if (null !== modelElem) {
      this.modelView = new ChoiceView(modelElem);
      this.modelView.onchange = this.onModelChange.bind(this);
    }

    if (null !== resetButton) {
      resetButton.addEventListener('click', this.onDeleteClick.bind(this));
    }

    this.data = {
      category: Number(this.el.getAttribute('data-category')),
      brand: null,
      model: null,
    };

    // load selected brand
    if (this.el.getAttribute('data-brand')) {
      this.brandId = Number(this.el.getAttribute('data-brand'));
      // load selected model
      if (this.el.getAttribute('data-model')) {
        this.modelId = Number(this.el.getAttribute('data-model'));
        this.setCookie(this.brandId, this.modelId);
      }
    } else if (this.useCookie) {
      // load last brand/model selection by cookie
      const cookieValue = this.getCookie();
      if (cookieValue) {
        const split = cookieValue.split('|');
        this.brandId = Number(split[0]);
        this.modelId = Number(split[1]);
      }
    }

    this.data.brand = null;
  }

  init() {
    if (this.hasModel && this.brandId) {
      this.data.brand = this.brandId;
      this.loadModels();
    }
  }

  getCookie() {
    const name = COOKIE_PREFIX + this.data.category + '=',
      decodedCookie = decodeURIComponent(document.cookie),
      ca = decodedCookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

  setCookie(brandId, modelId) {

    if (!this.hasModel) {
      modelId = 0;
    }

    const cookieName = COOKIE_PREFIX + this.data.category,
      value = brandId + '|' + modelId,
      date = new Date();

    date.setTime(date.getTime() + 24 * 60 * 60 * 1000); // 1 jours
    document.cookie = cookieName + '=' + value + ';path=/;expires=' + date.toUTCString();
  }

  loadModels() {
    if (!this.modelView) {
      return;
    }

    this.data.model = null;
    if (this.data.brand) {
      const xhr = new XMLHttpRequest(),
        params = '?category=' + this.data.category + '&brand=' + this.data.brand + '&model=' + this.data.model,
        url = global_url_base + '_category_pieces_oad/models' + params,
        modelView = this.modelView;
      let modelId = this.modelId;

      modelView.disable();

      xhr.onload = function () {
        if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
          const data = xhr.responseText,
            dataArray = JSON.parse(data);

          modelView.loadOptions(dataArray, modelId);
          modelView.render();
          modelView.enable();
          modelId = null;
        } else {
          modelView.loadOptions([], null);
          modelView.render();
          modelView.enable();
          modelId = null;
        }
      };

      xhr.open("GET", url, true);
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
      xhr.send();
    } else {
      this.modelView.loadOptions([], null);
      this.modelView.render();
    }
  }

  onModelChange(value: number) {
    this.data.model = value;
    if (value !== 0) {
      this.setCookie(this.data.brand, this.data.model);

      if (null !== this.submitButton) {
        this.submitButton.click();
      }
    }
  }

  onDeleteClick() {
    const cookieName = COOKIE_PREFIX + this.data.category,
      date = new Date();

    date.setTime(date.getTime() - 1000);
    document.cookie = cookieName + '=null;path=/;expires=' + date.toUTCString();
  }
}
